import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Loader2, Download, FileSpreadsheet, FileText, Edit, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";
import { useErpAuth } from "@/context/ErpAuthContext";

const MONTHS_RO = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const DAY_LETTERS = ["D", "L", "Ma", "Mi", "J", "V", "S"];
const PONTAJ_CODE_STYLE = {
  CO: "bg-green-100 text-green-700 font-bold",
  CM: "bg-red-100 text-red-600 font-bold",
  CFP: "bg-amber-100 text-amber-700 font-bold",
  CD: "bg-sky-100 text-sky-700 font-bold",
  CNC: "bg-slate-200 text-slate-600 font-bold",
  NM: "bg-slate-200 text-slate-600 font-bold",
  DEM: "bg-rose-200 text-rose-800 font-bold",
};
const codeStyle = (c) => PONTAJ_CODE_STYLE[c] || "bg-slate-100 text-slate-600 font-bold";
const displayName = (e) => `${e.nume || ""} ${e.prenume || ""}`.trim() || e.name || e.email;

export default function InvoiriTab() {
  const { hasPerm } = useErpAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({});
  const [pontaj, setPontaj] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [totalsAn, setTotalsAn] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [cell, setCell] = useState(null); // {emp, day, ore, motiv}
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canEdit = hasPerm("invoiri.gestionare");
  const daysInMonth = new Date(year, month, 0).getDate();
  const holidayDays = Object.fromEntries(holidays.map((h) => [h.zi, h.nume]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await erpApi.get(`/invoiri/${year}/${month}`);
      setEmployees(res.data.employees || []);
      setData(res.data.invoiri || {});
      setPontaj(res.data.pontaj || {});
      setHolidays(res.data.holidays || []);
      setTotalsAn(res.data.totals_an || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const isWeekend = (day) => {
    const d = new Date(year, month - 1, day).getDay();
    return d === 0 || d === 6;
  };
  const dayLetter = (day) => DAY_LETTERS[new Date(year, month - 1, day).getDay()];
  const isHoliday = (day) => day in holidayDays;

  const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
  const dateOf = (day) => new Date(year, month - 1, day);
  const isToday = (day) => {
    const d = dateOf(day);
    return d.getFullYear() === todayMid.getFullYear() && d.getMonth() === todayMid.getMonth() && d.getDate() === todayMid.getDate();
  };
  const isPast = (day) => dateOf(day) < todayMid;

  const dayInMonth = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr.slice(0, 10));
    if (isNaN(d)) return null;
    if (d.getFullYear() === year && d.getMonth() + 1 === month) return d.getDate();
    return null;
  };
  const empInMonth = (emp) => {
    const s = (emp.angajat_de_la || "").slice(0, 10);
    if (s) { const d = new Date(s); if (year < d.getFullYear() || (year === d.getFullYear() && month < d.getMonth() + 1)) return false; }
    const en = (emp.pontaj_pana_la || "").slice(0, 10);
    if (en) { const d = new Date(en); if (year > d.getFullYear() || (year === d.getFullYear() && month > d.getMonth() + 1)) return false; }
    return true;
  };

  const rows = employees
    .filter((e) => e.role !== "admin" && e.active !== false && e.in_pontaj !== false && empInMonth(e))
    .sort((a, b) => (parseInt(a.numar_contract) || 999999) - (parseInt(b.numar_contract) || 999999));

  const cellOre = (emp, day) => data[emp.id]?.[String(day)]?.ore || 0;
  const cellPontaj = (emp, day) => pontaj[emp.id]?.[String(day)] || null;

  const totalLuna = (emp) => {
    const hireDay = dayInMonth(emp.angajat_de_la);
    const exitDay = dayInMonth(emp.pontaj_pana_la);
    let t = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      if (hireDay && d < hireDay) continue;
      if (exitDay && d > exitDay) continue;
      t += cellOre(emp, d);
    }
    return t;
  };

  const goPrev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const goNext = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const goCurrent = () => { setMonth(now.getMonth() + 1); setYear(now.getFullYear()); };

  const onCellClick = (emp, day) => {
    if (!editMode || !canEdit) return;
    const c = data[emp.id]?.[String(day)];
    setCell({ emp, day, ore: c?.ore || "", motiv: c?.motiv || "" });
  };

  const saveCell = async () => {
    const { emp, day, ore, motiv } = cell;
    const val = parseInt(ore) || 0;
    if (val < 0 || val > 8) { toast.error("Orele trebuie să fie între 1 și 8"); return; }
    setSaving(true);
    try {
      await erpApi.post("/invoiri", { employee_id: emp.id, year, month, day, ore: val, motiv });
      toast.success(val > 0 ? "Învoire salvată" : "Învoire ștearsă");
      setCell(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doExport = async (kind) => {
    setExporting(true);
    try {
      const res = await erpApi.get(`/invoiri/export/${kind}/${year}/${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoiri_${year}_${String(month).padStart(2, "0")}.${kind === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export eșuat");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="invoiri-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="invoiri-prev" className="h-9 w-9">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center min-w-[160px]">
            <p className="font-display font-bold text-lg text-[#0B1424]">{MONTHS_RO[month - 1]} {year}</p>
          </div>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="invoiri-next" className="h-9 w-9">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goCurrent} className="text-slate-500">Azi</Button>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button size="sm" variant={editMode ? "default" : "outline"} onClick={() => setEditMode(!editMode)}
                    className={editMode ? "bg-[#2563EB] hover:bg-[#1D4ED8]" : ""} data-testid="invoiri-edit-toggle">
              {editMode ? <Check className="w-4 h-4 mr-1.5" /> : <Edit className="w-4 h-4 mr-1.5" />}
              {editMode ? "Gata" : "Editează"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => doExport("excel")} disabled={exporting} data-testid="invoiri-export-excel">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => doExport("pdf")} disabled={exporting} data-testid="invoiri-export-pdf">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />} PDF
          </Button>
        </div>
      </div>

      {editMode && (
        <p className="text-sm text-[#2563EB] bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          Mod editare activ — apasă pe o zi pentru a introduce orele de învoire (1–8). La 8 ore acumulate se consumă o zi de concediu.
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="text-xs border-collapse w-full border border-slate-300" data-testid="invoiri-table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-100 z-20 px-2 py-2 text-left font-semibold border border-slate-300 w-8">Nr.</th>
                <th className="sticky left-8 bg-slate-100 z-20 px-2 py-2 text-left font-semibold border border-slate-300 min-w-[160px]">Nume și prenume</th>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                  const we = isWeekend(day), hol = isHoliday(day), tod = isToday(day), past = isPast(day);
                  let hcls = "bg-slate-100 text-slate-700";
                  if (tod) hcls = "bg-blue-200 text-blue-900";
                  else if (hol) hcls = "bg-violet-300 text-violet-900";
                  else if (we) hcls = "bg-slate-300 text-slate-600";
                  else if (past) hcls = "bg-slate-200 text-slate-400";
                  return (
                    <th key={day} title={hol ? holidayDays[day] : ""}
                        className={`px-1 py-1 text-center font-medium border border-slate-300 min-w-[30px] ${hcls}`}>
                      <div>{day}</div>
                      <div className="text-[9px] font-normal opacity-70">{dayLetter(day)}</div>
                    </th>
                  );
                })}
                <th className="px-2 py-2 text-center font-semibold bg-orange-100 border border-slate-300 min-w-[48px]">Total ore</th>
                <th className="px-2 py-2 text-center font-semibold bg-green-100 border border-slate-300 min-w-[40px]" title="Zile de concediu consumate (cumulat pe an)">Zile CO</th>
                <th className="px-2 py-2 text-center font-semibold bg-blue-100 border border-slate-300 min-w-[40px]" title="Ore rămase sub 8 (cumulat pe an)">Ore răm.</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={daysInMonth + 5} className="text-center py-10 text-slate-400">Niciun angajat pentru această lună.</td></tr>
              ) : rows.map((emp, idx) => {
                const hireDay = dayInMonth(emp.angajat_de_la);
                const exitDay = dayInMonth(emp.pontaj_pana_la);
                const oreAn = totalsAn[emp.id] || 0;
                return (
                  <tr key={emp.id} className="hover:bg-slate-50">
                    <td className="sticky left-0 bg-white z-10 px-2 py-1.5 text-center border border-slate-300 font-medium text-slate-500">{idx + 1}</td>
                    <td className="sticky left-8 bg-white z-10 px-2 py-1.5 border border-slate-300 font-semibold text-[#0B1424] whitespace-nowrap">{displayName(emp)}</td>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                      const disabled = (hireDay && day < hireDay) || (exitDay && day > exitDay);
                      const ore = cellOre(emp, day);
                      const pcode = cellPontaj(emp, day);
                      const we = isWeekend(day), hol = isHoliday(day), tod = isToday(day), past = isPast(day);
                      let cls = "bg-white";
                      let content = "";
                      if (disabled) {
                        cls = "bg-slate-100 text-slate-300"; content = "X";
                      } else if (ore > 0) {
                        cls = "bg-orange-100 text-orange-700 font-bold"; content = ore;
                      } else if (pcode) {
                        cls = codeStyle(pcode); content = pcode;
                      } else if (hol) {
                        cls = "bg-violet-100 text-violet-400"; content = "X";
                      } else if (we) {
                        cls = "bg-slate-200 text-slate-400"; content = "X";
                      } else if (tod) {
                        cls = "bg-blue-50 ring-2 ring-blue-300 ring-inset";
                      } else if (past) {
                        cls = "bg-slate-50 text-slate-300";
                      }
                      const locked = disabled || (!!pcode && ore === 0);
                      return (
                        <td key={day} data-testid={`invoiri-cell-${emp.id}-${day}`}
                            title={pcode ? `Pontaj: ${pcode}` : ""}
                            onClick={() => !locked && onCellClick(emp, day)}
                            className={`px-1 py-1.5 text-center border border-slate-300 ${cls} ${editMode && canEdit && !locked ? "cursor-pointer hover:ring-2 hover:ring-[#2563EB] hover:ring-inset" : ""}`}>
                          {content}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center font-bold bg-orange-50 text-orange-800 border border-slate-300">{totalLuna(emp) || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-bold bg-green-50 text-green-700 border border-slate-300">{Math.floor(oreAn / 8) || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-blue-50 text-blue-700 border border-slate-300">{oreAn % 8 || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Orele din căsuțe = <b>învoirile</b> pe zi. <b className="text-slate-400">X</b> = zi liberă (weekend/sărbătoare). Codurile din Pontaj apar aici ca context: <b className="text-green-700">CO</b> = concediu odihnă, <b className="text-red-600">CM</b> = concediu medical, <b className="text-amber-700">CFP</b> = concediu fără plată, <b className="text-sky-700">CD</b>, <b className="text-slate-600">NM/CNC</b>. Zilele trecute apar <b className="text-slate-400">gri</b>, ziua curentă e <b className="text-blue-600">evidențiată</b>. <b>Zile CO</b> = total ore ÷ 8 (an); <b>Ore răm.</b> = restul sub 8.
      </p>

      {/* Dialog editare celulă */}
      <Dialog open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        <DialogContent className="max-w-sm" data-testid="invoiri-cell-dialog">
          <DialogHeader>
            <DialogTitle>
              {cell && `${displayName(cell.emp)} — ${cell.day} ${MONTHS_RO[month - 1]}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="inv-ore">Ore de învoire (0–8)</Label>
              <Input id="inv-ore" type="number" min={0} max={8} value={cell?.ore ?? ""}
                     onChange={(e) => setCell({ ...cell, ore: e.target.value })}
                     placeholder="ex. 2" className="mt-1.5" autoFocus data-testid="invoiri-ore-input" />
              <p className="text-xs text-slate-400 mt-1">Lasă 0 (sau gol) pentru a șterge învoirea din această zi.</p>
            </div>
            <div>
              <Label htmlFor="inv-motiv">Motiv (opțional)</Label>
              <Input id="inv-motiv" value={cell?.motiv ?? ""}
                     onChange={(e) => setCell({ ...cell, motiv: e.target.value })}
                     placeholder="ex. programare medicală" className="mt-1.5" data-testid="invoiri-motiv-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCell(null)}>Anulează</Button>
            <Button onClick={saveCell} disabled={saving} className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="invoiri-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
